import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Container,
  Grid,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import PublicIcon from "@mui/icons-material/Public";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import Header from "./Header";
import BottomNav from "./BottomNav";

const STORAGE_KEY = "chatio_saved_news";

function SavedNews() {
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    setSaved(stored);
  }, []);

  const removeArticle = (url) => {
    const updated = saved.filter((a) => a.url !== url);
    setSaved(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const clearAll = () => {
    setSaved([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <Header />
      <Container sx={{ mt: 16, mb: 12 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BookmarkIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Noticias guardadas
            </Typography>
            <Chip label={saved.length} size="small" color="primary" />
          </Box>
          {saved.length > 0 && (
            <Tooltip title="Eliminar todas">
              <IconButton onClick={clearAll} size="small" color="error">
                <DeleteSweepIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {saved.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: 10,
              gap: 2,
              color: "text.secondary",
            }}
          >
            <BookmarkBorderIcon sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="body1" color="text.secondary">
              No tenés noticias guardadas todavía.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Guardá noticias desde la pestaña <strong>Noticias</strong> en el inicio.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {saved.map((article, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    transition: "transform 0.15s ease",
                    "&:hover": { transform: "translateY(-2px)" },
                  }}
                >
                  <CardActionArea
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                  >
                    {article.image && (
                      <CardMedia
                        component="img"
                        height="180"
                        image={article.image}
                        alt={article.title}
                        sx={{ objectFit: "cover" }}
                      />
                    )}
                    <CardContent sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                        <PublicIcon sx={{ fontSize: 14, color: "#888" }} />
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 130 }}>
                          {article.source?.name}
                        </Typography>
                        {article.countryLabel && (
                          <Chip
                            label={article.countryLabel}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: "auto", fontSize: 10, height: 20 }}
                          />
                        )}
                      </Box>

                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.4 }}>
                        {article.title}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {article.description}
                      </Typography>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        {article.publishedAt
                          ? new Date(article.publishedAt).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </Typography>
                    </CardContent>
                  </CardActionArea>

                  {/* Remove bookmark */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 0.5 }}>
                    <Tooltip title="Quitar guardado">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => removeArticle(article.url)}
                      >
                        <BookmarkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
      <BottomNav />
    </>
  );
}

export default SavedNews;
